import React from 'react'
import { Button, Table, Container, Input, Row, Col } from 'reactstrap'
import SimpleDropdown from '../../view/DropDownView'
import {
    IoIosArrowUp,
    IoIosArrowDown,
} from 'react-icons/io'
const {
    getDrawingAttributes,
    insertDrawingAttribute,
    updateDrawingAttribute,
    deleteDrawingAttribute,
} = require('../../model/request/drawingAttributes')

const DATA_TYPES = ['string', 'number', 'boolean']

const emptyAttribute = () => ({
    key: '', data_type: 'string', unit: '', is_array: false, description: '', per_sign: false,
})

class DrawingAttributesAdminViewController extends React.Component {

    constructor(props) {
        super(props)
        this.state = {
            attributes: [],
            newAttribute: emptyAttribute(),
        }
    }

    componentDidMount() {
        this.refresh()
    }

    refresh() {
        getDrawingAttributes().then(
            (result) => this.setState(
                { attributes: result.data.attributes },
                () => this.normalizeOrderIndex(),
            ),
            (error) => this.props.showError(error),
        )
    }

    // Rows created before order_index existed (or before this feature) can
    // all share the same value (e.g. the column default), so swapping two
    // of them changes nothing persisted even though the move() logic itself
    // is correct. Self-heals on every load: whichever row's order_index
    // doesn't match its actual position (already ORDER BY order_index, key
    // from the backend) gets corrected to match and persisted, so every
    // row ends up with a distinct, position-matching value future swaps
    // can actually change.
    normalizeOrderIndex() {
        const attributes = this.state.attributes
        const mismatched = attributes
            .map((attribute, index) => ({ attribute, index }))
            .filter(({ attribute, index }) => attribute.order_index !== index)

        if (mismatched.length === 0) return

        mismatched.forEach(({ attribute, index }) => {
            attribute.order_index = index
        })
        this.setState({ attributes })

        Promise.all(
            mismatched.map(({ attribute }) => updateDrawingAttribute(attribute.key, attribute))
        ).catch((error) => this.props.showError(error))
    }

    move(index, direction) {
        const swapIndex = direction === 'up' ? index - 1 : index + 1
        if (swapIndex < 0 || swapIndex >= this.state.attributes.length) return

        const attributes = [...this.state.attributes]
        const current = { ...attributes[index] }
        const neighbor = { ...attributes[swapIndex] }
        const swappedOrderIndex = current.order_index
        current.order_index = neighbor.order_index
        neighbor.order_index = swappedOrderIndex
        attributes[index] = neighbor
        attributes[swapIndex] = current
        this.setState({ attributes }, () => this.normalizeOrderIndex())

        Promise.all([
            updateDrawingAttribute(current.key, current),
            updateDrawingAttribute(neighbor.key, neighbor),
        ]).catch((error) => this.props.showError(error))
    }

    saveNew() {
        if (!this.state.newAttribute.key.trim()) return
        insertDrawingAttribute(this.state.newAttribute).then(
            // Refresh instead of appending the local newAttribute object -
            // it never had an order_index (that's assigned server-side via
            // MAX(order_index)+1), so appending it directly left the new
            // row with order_index undefined until a full page reload,
            // which then got sent as null on its next save.
            () => this.setState({ newAttribute: emptyAttribute() }, () => this.refresh()),
            (error) => this.props.showError(error),
        )
    }

    save(attribute) {
        this.props.turnOnLoader()
        updateDrawingAttribute(attribute.key, attribute).then(
            () => this.props.turnOffLoader(),
            (error) => {
                this.props.showError(error)
                this.props.turnOffLoader()
            },
        )
    }

    delete(key) {
        deleteDrawingAttribute(key).then(
            () => this.setState((s) => ({
                attributes: s.attributes.filter((a) => a.key !== key),
            })),
            (error) => this.props.showError(error),
        )
    }

    updateField(key, field, value) {
        const attributes = this.state.attributes.map((a) =>
            a.key === key ? { ...a, [field]: value } : a
        )
        this.setState({ attributes })
    }

    renderDataTypeDropdown(value, onChange) {
        return <SimpleDropdown
            index={DATA_TYPES.indexOf(value)}
            values={DATA_TYPES}
            onChange={(i) => onChange(DATA_TYPES[i])}
        />
    }

    render() {
        return (
            <Container>
                <h3 style={{ marginTop: '20px', marginBottom: '20px' }}>Drawing Attributes</h3>
                <Row><Col><div style={{'overflowX': 'auto'}}><Table>
                    <thead>
                        <tr>
                            <th style={{ width: '50px' }}>Order</th>
                            <th>Key</th>
                            <th style={{ width: '120px' }}>Type</th>
                            <th style={{ width: '100px' }}>Unit</th>
                            <th style={{ width: '80px' }}>Array</th>
                            <th style={{ width: '90px' }}>Per Sign</th>
                            <th>Description</th>
                            <th style={{ width: '80px' }}>Save</th>
                            <th style={{ width: '80px' }}>Delete</th>
                        </tr>
                    </thead>
                    <tbody>
                        {this.state.attributes.map((attribute, index) => (
                            <tr key={attribute.key}>
                                <td>
                                    <Button
                                        id={'move-attribute-up-button-' + index}
                                        style={{
                                            'height': '10pt',
                                            'margin': '0pt',
                                            'padding': '0pt',
                                        }}
                                        size='sm'
                                        disabled={index === 0}
                                        onClick={() => this.move(index, 'up')}>
                                        <IoIosArrowUp
                                            style={{
                                                'margin': '0pt',
                                                'padding': '0pt',
                                                'fontSize': '10pt',
                                                'fontWeight': 'bold',
                                            }}/>
                                    </Button>
                                    <Button
                                        id={'move-attribute-down-button-' + index}
                                        style={{
                                            'height': '10pt',
                                            'margin': '0pt',
                                            'padding': '0pt',
                                        }}
                                        size='sm'
                                        disabled={index === this.state.attributes.length - 1}
                                        onClick={() => this.move(index, 'down')}>
                                        <IoIosArrowDown
                                            style={{
                                                'margin': '0pt',
                                                'padding': '0pt',
                                                'fontSize': '10pt',
                                                'fontWeight': 'bold',
                                            }}/>
                                    </Button>
                                </td>
                                <td>{attribute.key}</td>
                                <td>
                                    {this.renderDataTypeDropdown(
                                        attribute.data_type,
                                        (v) => this.updateField(attribute.key, 'data_type', v))}
                                </td>
                                <td>
                                    <Input
                                        value={attribute.unit || ''}
                                        onChange={(e) => this.updateField(
                                            attribute.key, 'unit', e.target.value)}
                                    />
                                </td>
                                <td>
                                    <Input
                                        type="checkbox"
                                        checked={attribute.is_array}
                                        onChange={(e) => this.updateField(
                                            attribute.key, 'is_array', e.target.checked)}
                                    />
                                </td>
                                <td>
                                    <Input
                                        type="checkbox"
                                        checked={attribute.per_sign}
                                        onChange={(e) => this.updateField(
                                            attribute.key, 'per_sign', e.target.checked)}
                                    />
                                </td>
                                <td>
                                    <Input
                                        value={attribute.description}
                                        onChange={(e) => this.updateField(
                                            attribute.key, 'description', e.target.value)}
                                    />
                                </td>
                                <td>
                                    <Button color="primary" onClick={() => this.save(attribute)}>Save</Button>
                                </td>
                                <td>
                                    <Button color="danger" onClick={() => this.delete(attribute.key)}>Delete</Button>
                                </td>
                            </tr>
                        ))}
                        <tr>
                            <td></td>
                            <td>
                                <Input
                                    placeholder="e.g. width"
                                    value={this.state.newAttribute.key}
                                    onChange={(e) => this.setState({
                                        newAttribute: { ...this.state.newAttribute, key: e.target.value },
                                    })}
                                />
                            </td>
                            <td>
                                {this.renderDataTypeDropdown(
                                    this.state.newAttribute.data_type,
                                    (v) => this.setState({
                                        newAttribute: { ...this.state.newAttribute, data_type: v },
                                    }))}
                            </td>
                            <td>
                                <Input
                                    placeholder="in"
                                    value={this.state.newAttribute.unit}
                                    onChange={(e) => this.setState({
                                        newAttribute: { ...this.state.newAttribute, unit: e.target.value },
                                    })}
                                />
                            </td>
                            <td>
                                <Input
                                    type="checkbox"
                                    checked={this.state.newAttribute.is_array}
                                    onChange={(e) => this.setState({
                                        newAttribute: { ...this.state.newAttribute, is_array: e.target.checked },
                                    })}
                                />
                            </td>
                            <td>
                                <Input
                                    type="checkbox"
                                    checked={this.state.newAttribute.per_sign}
                                    onChange={(e) => this.setState({
                                        newAttribute: { ...this.state.newAttribute, per_sign: e.target.checked },
                                    })}
                                />
                            </td>
                            <td>
                                <Input
                                    placeholder="What this field means on a drawing"
                                    value={this.state.newAttribute.description}
                                    onChange={(e) => this.setState({
                                        newAttribute: { ...this.state.newAttribute, description: e.target.value },
                                    })}
                                />
                            </td>
                            <td>
                                <Button color="success" onClick={() => this.saveNew()}>Add</Button>
                            </td>
                            <td></td>
                        </tr>
                    </tbody>
                </Table></div></Col></Row>
            </Container>
        )
    }
}

export default DrawingAttributesAdminViewController
